<template>
  <div>
    <v-card class="my-1">
      <v-card-title>Monthly Payroll Summary By Company </v-card-title>
      <v-row class="px-4">
        <v-col cols="10">
          <v-row>
            <v-col cols="3">
              <v-menu
                v-model="startMonthMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startMonthDisplay"
                    v-validate="'required'"
                    :error-messages="errors.collect('startMonthDisplay')" 
                    data-vv-name="startMonthDisplay"
                    label="From Payroll Period"
                    readonly
                    data-vv-as="start month"  
                    class="label-position"   
                    :class="{
                      'required':!startMonthDisplay
                    }"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="fromPayrollPeriod"
                  type="month"
                  :allowed-dates="allowedMonths"
                  @input="startMonthMenu = false;"
                />
              </v-menu>
            </v-col>

            <v-col cols="3">
              <v-menu
                v-model="endMonthMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endMonthDisplay"
                    v-validate="'required'"
                    :disabled="!fromPayrollPeriod"
                    :error-messages="errors.collect('endMonthDisplay')" 
                    data-vv-name="endMonthDisplay"
                    label="To Payroll Period"
                    readonly
                    data-vv-as="end month"  
                    class="label-position"   
                    :class="{
                      'required':!endMonthDisplay
                    }"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="toPayrollPeriod"
                  type="month"
                  :min="fromPayrollPeriod"
                  :allowed-dates="allowedMonths"
                  @input="endMonthMenu = false;"
                />
              </v-menu>
            </v-col>

            <v-col cols="6">
              <v-autocomplete
                v-model="payrollCycleIds"
                v-validate="'required'"
                :loading="dropdownLoading"
                label="Payroll Cycles"
                item-text="payroll_cycle.name"
                item-value="payroll_cycle.id"
                :items="filteredPayrollCycles"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('payrollCycleIds')"
                data-vv-name="payrollCycleIds"  
                data-vv-as="payroll cycles"
              />              
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedGroups"
                :items="groupOptions"
                label="Company Groups"
                item-text="text"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                clearable
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                :loading="dropdownLoading"
                label="Companies"
                item-text="name"
                item-value="id"
                :items="companyOptions"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('selectedCompanies')"
                data-vv-name="selectedCompanies"  
                data-vv-as="companies"
              /> 
            </v-col>           
          </v-row>
          <v-row>
            <v-col>
              <v-checkbox v-model="showHeadcount">
                <template v-slot:label>
                  <div class="font-weight-bold">
                    Show Headcount in Report
                  </div>
                </template>
              </v-checkbox>
            </v-col>  
          </v-row>
        </v-col>

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="loading" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>
      
          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="loading"
                @click="generateMonthlyPayrollReportByCompany"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      payrollPeriods: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        selectedGroups: [],
        selectedCompanies: [],
        dropdownLoading: false,
        progressbar: 0,
        selectedPayrollPeriod: null,
        payrollCycleIds: null,
        showHeadcount: true,
        fromPayrollPeriod: null,
        toPayrollPeriod: null,
        startMonthMenu: false,
        endMonthMenu: false,
      }
    },
    computed: {
      allowedMonths () {
        const payrollMonths = this.payrollPeriods.map((period) =>
          period.payroll_date.slice(0, 7)
        )
        return (date) => payrollMonths.includes(date.slice(0, 7))
      },
      startMonthDisplay () {
        return this.fromPayrollPeriod ? this.$moment(this.fromPayrollPeriod, 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      endMonthDisplay () {
        return this.toPayrollPeriod ? this.$moment(this.toPayrollPeriod, 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      filteredPayrollPeriods () {
        if (!this.fromPayrollPeriod || !this.toPayrollPeriod) return []

        const startDate = new Date(this.fromPayrollPeriod)
        const endDate = new Date(this.toPayrollPeriod)

        return this.payrollPeriods
          .filter((period) => {
            const payrollDate = new Date(period.payroll_date)
            return payrollDate >= startDate && payrollDate <= endDate
          })
      },
      filteredPayrollCycles () {
        const payrollCycleTransactions = this.filteredPayrollPeriods
          .flatMap(period => period.payroll_cycle_transactions)

        const uniqueCycles = payrollCycleTransactions.reduce((acc, transaction) => {
          if (!acc.some(item => item.payroll_cycle.id === transaction.payroll_cycle.id)) {
            acc.push(transaction)
          }
          return acc
        }, [])

        return uniqueCycles
      },
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },
    },
    watch: {
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    created () {
      this.dropdownLoading = true

      if (this.payrollPeriods.length > 0) {
        this.fromPayrollPeriod = this.payrollPeriods[0].payroll_date.slice(0, 7)
        this.toPayrollPeriod = this.payrollPeriods[0].payroll_date.slice(0, 7)
      }

      this.dropdownLoading = false
    },
    methods: {
      generateMonthlyPayrollReportByCompany () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.loading = true

            this.$api.payrollReports().monthlyPayrollReportByCompany(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'MonthlyPayrollReportByCompany - ' + this.$moment(this.fromPayrollPeriod).format('MMM YYYY') + ' to ' + this.$moment(this.toPayrollPeriod).format('MMM YYYY') + ' [' + this.$moment().format('YYYY-MM-DD h.mma') + '].xlsx') 
                link.click()
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'Report downloaded successfully' })
              })
              .catch(error => {
                this.loading = false
                if (error.response.status === 400) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.status, 
                  })
                } else {
                  this.$snack({ color: 'error', message: 'Error! Please contact support' })
                }
              })
          })
      },
      prepareFormData () {
        return {
          payrollPeriods: this.filteredPayrollPeriods,
          payrollCycleIds: this.payrollCycleIds,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies,
          showHeadcount: this.showHeadcount,          
        }
      },
    },
  }
</script>
